import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { AppearanceProvider } from './context/AppearanceContext'
import { LanguageProvider } from './context/LanguageContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import PendingPromotionCodeBanner from './components/account/PendingPromotionCodeBanner'
import PromotionAttributionCapture from './components/account/PromotionAttributionCapture'
import CombineLaunchShortcut from './components/coach/CombineLaunchShortcut'
import PendingClubWarInviteBanner from './components/coach/PendingClubWarInviteBanner'
import ArgusSurfaceRouteMarker from './components/layout/ArgusSurfaceRouteMarker'
import OnboardingTransitionRouter from './components/onboarding/OnboardingTransitionRouter'
import AthleteGrowthShortcut from './components/player/AthleteGrowthShortcut'
import PendingCombineClaimBanner from './components/player/PendingCombineClaimBanner'
import ArgusStatePanel from './components/ui/ArgusStatePanel'

const LandingPage = lazy(() => import('./pages/LandingPage'))
const AboutArgusPage = lazy(() => import('./pages/AboutArgusPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const PromotionCodePage = lazy(() => import('./pages/PromotionCodePage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const OnboardingSportsPage = lazy(() => import('./pages/OnboardingSportsPage'))
const OnboardingOrganizationPage = lazy(() => import('./pages/OnboardingOrganizationPage'))
const OnboardingConsentPage = lazy(() => import('./pages/OnboardingConsentPage'))
const CampaignOwnerOnboardingPage = lazy(() => import('./pages/CampaignOwnerOnboardingPage'))
const CampaignOwnerReviewPage = lazy(() => import('./pages/CampaignOwnerReviewPage'))
const CampaignReviewPage = lazy(() => import('./pages/CampaignReviewPage'))
const AdminFounderReferralConsolePage = lazy(
  () => import('./pages/AdminFounderReferralConsolePage'),
)
const AdminReferralActivationPage = lazy(() => import('./pages/AdminReferralActivationPage'))
const AdminGrowthIntelligencePage = lazy(() => import('./pages/AdminGrowthIntelligencePage'))
const PartnerProfilePage = lazy(() => import('./pages/PartnerProfilePage'))
const CoachDirectoryPage = lazy(() => import('./pages/CoachDirectoryPage'))
const CoachProfilePublicPage = lazy(() => import('./pages/CoachProfilePublicPage'))
const AthletePassportPublicPage = lazy(() => import('./pages/AthletePassportPublicPage'))
const CombineClaimPage = lazy(() => import('./pages/CombineClaimPage'))
const ClubWarJoinPage = lazy(() => import('./pages/ClubWarJoinPage'))
const ClubWarStandingsPage = lazy(() => import('./pages/ClubWarStandingsPage'))
const CoachCombinePage = lazy(() => import('./pages/CoachCombinePage'))
const CoachClubWarsPage = lazy(() => import('./pages/CoachClubWarsPage'))
const AthleteGrowthLoopPage = lazy(() => import('./pages/AthleteGrowthLoopPage'))
const AttributeLibraryPage = lazy(() => import('./pages/AttributeLibraryPage'))
const SocialIdentityPage = lazy(() => import('./pages/SocialIdentityPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const MyPlanPage = lazy(() => import('./pages/MyPlanPage'))
const ReferralRewardsPage = lazy(() => import('./pages/ReferralRewardsPage'))
const PlayerDashboard = lazy(() => import('./pages/PlayerDashboard'))
const CoachDashboard = lazy(() => import('./pages/CoachDashboardV2'))
const ClubDashboard = lazy(() => import('./pages/ClubDashboard'))
const SponsorDashboard = lazy(() => import('./pages/SponsorDashboard'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

function PageLoader() {
  return (
    <ArgusStatePanel
      variant="loading"
      eyebrow="ARGUS // WORKSPACE"
      title="Loading your cockpit"
      message="ARGUS is assembling the current workspace and preserving your active route."
      fullScreen
    />
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppearanceProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <ArgusSurfaceRouteMarker />
              <OnboardingTransitionRouter />
              <PromotionAttributionCapture />
              <PendingCombineClaimBanner />
              <PendingClubWarInviteBanner />
              <PendingPromotionCodeBanner />
              <CombineLaunchShortcut />
              <AthleteGrowthShortcut />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/about" element={<AboutArgusPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/promotions" element={<PromotionCodePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/partners/:slug" element={<PartnerProfilePage />} />
                  <Route path="/coaches" element={<CoachDirectoryPage />} />
                  <Route path="/coaches/:slug" element={<CoachProfilePublicPage />} />
                  <Route path="/a/:slug" element={<AthletePassportPublicPage />} />
                  <Route path="/combine/claim" element={<CombineClaimPage />} />
                  <Route path="/club-war/join" element={<ClubWarJoinPage />} />
                  <Route path="/club-war/:id" element={<ClubWarStandingsPage />} />
                  <Route element={<ProtectedRoute />}>
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/onboarding/sports" element={<OnboardingSportsPage />} />
                    <Route
                      path="/onboarding/organization"
                      element={<OnboardingOrganizationPage />}
                    />
                    <Route path="/onboarding/consent" element={<OnboardingConsentPage />} />
                    <Route path="/account/plan" element={<MyPlanPage />} />
                    <Route path="/account/referrals" element={<ReferralRewardsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/player/profile" element={<ProfilePage />} />
                    <Route path="/athlete/profile" element={<ProfilePage />} />
                    <Route path="/coach/profile" element={<ProfilePage />} />
                    <Route path="/club/profile" element={<ProfilePage />} />
                    <Route path="/sponsor/profile" element={<ProfilePage />} />
                    <Route path="/partner/profile" element={<ProfilePage />} />
                    <Route path="/attributes" element={<AttributeLibraryPage />} />
                    <Route path="/identity/social" element={<SocialIdentityPage />} />
                    <Route path="/coach/identity" element={<SocialIdentityPage />} />
                    <Route path="/club/identity" element={<SocialIdentityPage />} />
                    <Route path="/sponsor/onboarding" element={<CampaignOwnerOnboardingPage />} />
                    <Route path="/admin/campaign-owners" element={<CampaignOwnerReviewPage />} />
                    <Route path="/admin/campaigns" element={<CampaignReviewPage />} />
                    <Route path="/admin/referrals" element={<AdminFounderReferralConsolePage />} />
                    <Route
                      path="/admin/referrals/activation"
                      element={<AdminReferralActivationPage />}
                    />
                    <Route path="/admin/growth" element={<AdminGrowthIntelligencePage />} />
                    <Route path="/player" element={<PlayerDashboard />} />
                    <Route path="/athlete" element={<PlayerDashboard />} />
                    <Route path="/athlete/growth" element={<AthleteGrowthLoopPage />} />
                    <Route path="/coach" element={<CoachDashboard />} />
                    <Route path="/coach/combine" element={<CoachCombinePage />} />
                    <Route path="/coach/club-wars" element={<CoachClubWarsPage />} />
                    <Route path="/club" element={<ClubDashboard />} />
                    <Route path="/sponsor" element={<SponsorDashboard />} />
                    <Route path="/partner" element={<SponsorDashboard />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </AppearanceProvider>
    </ErrorBoundary>
  )
}
